import React, {useMemo} from "react";
import {Context} from "@hotwired/stimulus";
import {useForm, useWatch} from "react-hook-form";
import {valibotResolver} from "@hookform/resolvers/valibot";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import {Form, FormControl, FormField, FormItem, FormLabel,} from "@root/server/client/esweb/components/ui/form.tsx";
import {FormInput} from "@root/server/client/esweb/components/ui/rhf/FormInput.tsx";
import {FormSelect} from "@root/server/client/esweb/components/ui/rhf/FormSelect.tsx";
import {FormServerErrors} from "@root/server/client/esweb/src/pages/web/shared/form-server-errors.tsx";
import {useFormSubmission} from "@root/server/client/esweb/src/lib/hooks/useFormSubmission.ts";
import {SaveSubmitButton} from "@root/server/client/esweb/src/pages/web/shared/continue-submit-button.tsx";
import {
    hook_filter_form_name,
    HookFilterEditFormValues,
    HookFilterFormProps,
    HookFilterFormSchema
} from "./hook_filter_form.shared.ts";
import {FormTextarea} from "@root/server/client/esweb/components/ui/rhf/FormTextarea.tsx";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@root/server/client/esweb/components/ui/accordion.tsx";
import {Badge} from "@root/server/client/esweb/components/ui/badge.tsx";
import {Switch} from "@root/server/client/esweb/components/ui/switch.tsx";
import {Button} from "@root/server/client/esweb/components/ui/button.tsx";
import {Input} from "@root/server/client/esweb/components/ui/input.tsx";
import {Label} from "@root/server/client/esweb/components/ui/label.tsx";
import {Eye, Info, Trash2} from "lucide-react";
import {CopyToClipboardButton} from "@root/server/client/esweb/components/ui/copy_to_clipboard.stimulus.tsx";

export const name = hook_filter_form_name;

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <HookFilterForm {...props} />);
    }
}

function HookFilterForm({ submitUrl, initialData, aiModels = [], plan, apiBase }: HookFilterFormProps & { aiModels: any[], plan: string, apiBase: string }) {
    const defaultValues = useMemo<HookFilterEditFormValues>(() => ({
        name: initialData?.name ?? '',
        lucene_q: initialData?.lucene_q ?? "",
        prompt: initialData?.prompt ?? "",
        ai_model_id: initialData?.ai_model_id ?? (aiModels.length > 0 ? aiModels[0].id : ""),

        heartbeat_m: initialData?.heartbeat_m ?? "",

        ds_ntfy_url: initialData?.ds_ntfy_url ?? "",
        ds_webhook_url: initialData?.ds_webhook_url ?? "",
        ds_email: initialData?.ds_email ?? "",
        process_images: initialData?.process_images ?? false,
        dedupe_key: initialData?.dedupe_key ?? "id",
    }), [initialData, aiModels]);

    const form = useForm({
        resolver: valibotResolver(HookFilterFormSchema),
        defaultValues,
        mode: "onTouched",
    });

    const { control, handleSubmit } = form;
    const { execute, isSubmitting, canSubmit, serverErrors } = useFormSubmission({ submitUrl });

    const isEdit = !!initialData?.id;

    const ai_model_id = useWatch({ control, name: "ai_model_id" });
    const selectedModel = aiModels?.find(m => m.id === ai_model_id);
    const showProcessImages = selectedModel?.image_support;

    const filterUrl = isEdit && apiBase ? `${apiBase}/api/filter/${initialData.id}` : '';

    return (
        <Form {...form}>
            <form onSubmit={handleSubmit(execute)} className="space-y-6">
                
                {isEdit && (
                    <div className="space-y-2">
                        <Label>Filter URL</Label>
                        <div className="flex space-x-2">
                            <Input value={filterUrl} readOnly className="bg-muted text-muted-foreground" />
                            <CopyToClipboardButton text={filterUrl} />
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-6">
                    <FormInput control={control} name="name" label="Name" placeholder="e.g. Car leads" />

                    <div className="space-y-2">
                         <div className="flex items-center gap-2">
                            <FormLabel>Lucene Query</FormLabel>
                             <Badge variant="secondary" className="text-[10px] px-1 h-5">1 Credit (charged every time)</Badge>
                         </div>
                         <FormInput
                            control={control}
                            name="lucene_q"
                            placeholder='e.g. ("car" OR "auto") AND "sale" -rental'
                        />
                         <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="help" className="border-b-0">
                                <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:no-underline">
                                    <div className="flex items-center gap-1">
                                        <Info className="h-3 w-3" />
                                        <span>How to filter?</span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="text-sm text-muted-foreground">
                                    <p>Ask your favorite chatbot how to define your keyword requirements using Lucene syntax with `liqe` library without regexes max 500 chars.</p>
                                    <p className="mt-1">
                                        You can check your json keys as <code className="bg-muted px-1 rounded">my_key:"somevalue"</code> or the raw text of the body as <code className="bg-muted px-1 rounded">__body:"full body text"</code>.
                                    </p>
                                    <p className="mt-1">
                                        Example: <code className="bg-muted px-1 rounded">(title:React OR title:Vue) AND remote:true AND salary:&gt;120000 AND NOT title:Manager AND NOT description:"crunch time"</code>
                                    </p>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </div>

                    <div className="space-y-2">
                         <div className="flex items-center gap-2">
                            <FormLabel>Deduplication Key</FormLabel>
                         </div>
                         <FormInput
                            control={control}
                            name="dedupe_key"
                            placeholder="e.g. id, url, or nested.key"
                        />
                         <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="help" className="border-b-0">
                                <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:no-underline">
                                    <div className="flex items-center gap-1">
                                        <Info className="h-3 w-3" />
                                        <span>What is deduplication?</span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="text-sm text-muted-foreground">
                                    <p>Deduplication uses 48h sliding window pattern, when no duplicates for given id are received for 48h hours, the id is forgotten.</p>
                                    <p className="mt-1">
                                        Specify the key in your JSON payload that uniquely identifies the item (e.g., <code className="bg-muted px-1 rounded">id</code> or <code className="bg-muted px-1 rounded">url</code>).
                                    </p>
                                    <p className="mt-1">
                                        If the key on payload is undefined, null or "", it will be ignored and <code className="bg-muted px-1 rounded">__body</code> (full raw body string) will be used instead.
                                    </p>
                                    <p className="mt-1">
                                        If you leave this field empty, there will be no deduplication.
                                    </p>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <FormLabel>AI Prompt</FormLabel>
                            <Badge variant="secondary" className="text-[10px] px-1 h-5">1+ credits</Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground italic">Charged only if keyword filter matches based on token usage.</p>
                        <FormTextarea control={control} name="prompt" placeholder="Instructions for the filter..." className="min-h-[100px]" />

                         <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="help" className="border-b-0">
                                <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:no-underline">
                                    <div className="flex items-center gap-1">
                                        <Info className="h-3 w-3" />
                                        <span>Prompting Guide & Data Transformation</span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="text-sm text-muted-foreground space-y-2">
                                    <p>
                                        Explain what are you interested in. You can also ask to extract some data and it will be added to the request for the <b>Downstream webhook</b> in the <code className="bg-muted px-1 rounded">user_data</code> field (if input is JSON).
                                    </p>
                                    {/*<p>*/}
                                    {/*    <a href="#" className="underline text-primary">View Demos & Examples</a>*/}
                                    {/*</p>*/}
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                        
                        <div className="pt-2">
                             <FormSelect
                                control={control}
                                name="ai_model_id"
                                label="AI Model"
                                options={aiModels.map(m => {
                                    const isAllowed = !m.plans || m.plans.includes(plan as any);
                                    const inputCredits = Math.ceil(Number(m.input_cost_per_1m) * 20000);
                                    const outputCredits = Math.ceil(Number(m.output_cost_per_1m) * 20000);

                                    return {
                                        label: (
                                            <div className="flex items-center justify-between w-full gap-4 py-1">
                                                <div className="flex flex-col text-left">
                                                    <span className="font-medium text-sm">{m.title}</span>
                                                    {m.subtitle && <span className="text-xs font-light">{m.subtitle}</span>}
                                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                                        {inputCredits} in / {outputCredits} out credits per 1M tokens
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-1.5">
                                                    <div className="flex items-center gap-1">
                                                        {m.image_support && <Badge variant="outline" className="text-[10px] px-1 h-5 bg-white"><Eye className="w-3 h-3 mr-1"/> Vision</Badge>}
                                                        {m.plans.map((p: string) => <Badge key={p} variant="default" className="text-[10px] px-1 h-5">{p}</Badge>)}
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                        value: m.id,
                                        disabled: !isAllowed
                                    };
                                })}
                            />
                        </div>
                    </div>

                    {showProcessImages && (
                    <FormField
                        control={control}
                        name="process_images"
                        render={({ field }) => (
                            <FormItem className="flex flex-col gap-2 rounded-lg border p-4">
                                <div className="flex flex-row items-center justify-between">
                                    <div className="space-y-0.5">
                                        <div className="flex items-center gap-2">
                                            <FormLabel className="text-base">Process Images</FormLabel>
                                            <Badge variant="default" className="text-[10px] px-1 h-5">PRO</Badge>
                                        </div>
                                        <p className="text-[11px] text-muted-foreground italic">Charged only if keyword filter matches</p>
                                    </div>
                                    <FormControl>
                                        <Switch
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                </div>
                                 <div className="text-sm text-muted-foreground">
                                    Analyze images in the payload. Simply include an <code className="bg-muted px-1 rounded">images</code> key in your JSON payload.
                                    <br/>
                                    Valid images are available in <code>validated_data.images</code> in the downstream webhook.
                                    <pre className="mt-2 bg-muted p-2 rounded text-xs overflow-x-auto">
                                        {`{ "images": [{ "src": "https://..." }] }`}
                                    </pre>
                                </div>
                            </FormItem>
                        )}
                    />
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                         <div className="space-y-2">
                            <FormInput control={control} name="heartbeat_m" type="number" label="Heartbeat (minutes)" />
                            <p className="text-xs text-muted-foreground">
                                Receive a notification if the filter does not receive any calls in the configured time.
                            </p>
                         </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <FormLabel>Notification Email</FormLabel>
                                <Badge variant="secondary" className="text-[10px] px-1 h-5">10 Credits</Badge>
                                <Badge variant="default" className="text-[10px] px-1 h-5">PRO</Badge>
                            </div>
                            <FormInput control={control} name="ds_email" />
                            <p className="text-xs text-orange-600 dark:text-orange-400">
                                Sends an email for every single matched event without aggregation. Not recommended for high volume.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <FormInput control={control} name="ds_ntfy_url" label="ntfy.sh URL" placeholder="https://ntfy.sh/..." />
                             <p className="text-xs text-muted-foreground">
                                Simple push notifications. <a href="https://ntfy.sh/" target="_blank" rel="noreferrer" className="underline hover:text-primary">Download the app</a>.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <FormLabel>Downstream webhook</FormLabel>
                                <Badge variant="default" className="text-[10px] px-1 h-5">PRO</Badge>
                            </div>
                            <FormInput control={control} name="ds_webhook_url" placeholder="https://nexthook.any/..." />
                            <Accordion type="single" collapsible className="w-full">
                                <AccordionItem value="help" className="border-b-0">
                                    <AccordionTrigger className="py-1 text-xs text-muted-foreground hover:no-underline justify-start gap-1">
                                         <Info className="h-3 w-3" /> Details
                                    </AccordionTrigger>
                                    <AccordionContent className="text-xs text-muted-foreground">
                                        <p>Original headers and body are forwarded.</p>
                                        <p className="mt-1">If the input is JSON and AI/Validation data is present, it will be injected into the payload as <code>user_data</code> and <code>validated_data</code> fields.</p>
                                        <p className="mt-1">Override this field by adding query param <code className="bg-muted px-1 rounded">ds_wh=URL</code> to the filter call.</p>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </div>
                </div>

                <FormServerErrors errors={serverErrors} />
                <SaveSubmitButton isSubmitting={isSubmitting} canSubmit={canSubmit} />

                {isEdit && <FilterDeleteButton deleteUrl={`/app/hook_filters/${initialData.id}`}/>}
            </form>
        </Form>
    );
}


function FilterDeleteButton(props: {deleteUrl: string}) {
    const {deleteUrl} = props;

    const { execute: deleteExecute, isSubmitting } = useFormSubmission({ submitUrl: deleteUrl, method: 'DELETE' });

    return <div className="border-t">
        <Button
            type="button"
            variant="destructive"
            className="w-full"
            disabled={isSubmitting}
            onClick={() => {
                if (confirm("Are you sure you want to delete this filter? This action cannot be undone.")) {
                    deleteExecute({});
                }
            }}
        >
            <Trash2 className="w-4 h-4 mr-2" />
            {isSubmitting ? "Deleting..." : "Delete Filter"}
        </Button>
    </div>
}
